import { useEffect, useState } from 'react';
import {
  useCreate,
  useMany,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import { Form, Modal, Radio, Select } from 'antd';
import type {
  OrganizationMemberCreateIn,
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { OrganizationRole } from '@/api/generated/schemas';
import { useOrganizationPublisherLinks } from '@/hooks/tenancy/useOrganizationPublisherLinks';
import { extractBackendMessage } from '@/utils/apiError';

type AddMemberValues = {
  user_id: number;
  publisher_id?: number | null;
  role: OrganizationRole;
};

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  record: OrganizationOut;
  onMembersChanged: () => void;
}

export const AddMemberModal = ({
  open,
  onClose,
  record,
  onMembersChanged,
}: AddMemberModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCreate();
  const [form] = Form.useForm<AddMemberValues>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string) =>
    t(`tenancy.organizations.members.add.${suffix}`);

  const { selectProps: userSelectProps } = useSelect({
    resource: 'iam.users',
    optionLabel: 'username',
    optionValue: 'id',
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  // Members may only be linked to the organization's own parent publishers —
  // in it now or joining later. `useMany` fetches by id and takes no filters,
  // so imprints drop out here.
  const { publisherIds, isLoading: linksLoading } =
    useOrganizationPublisherLinks(record.id, { enabled: open });
  const { result: publishersResult, query: publishersQuery } =
    useMany<PublisherListOut>({
      resource: 'user_catalog.publishers',
      ids: publisherIds,
      queryOptions: { enabled: open && publisherIds.length > 0 },
    });
  const publisherOptions = (publishersResult?.data ?? [])
    .filter((p: PublisherListOut) => p.parent_id == null)
    .map((p: PublisherListOut) => ({ value: p.id, label: p.name_with_role }));

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: AddMemberValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload: OrganizationMemberCreateIn = {
      organization_id: record.id,
      user_id: values.user_id,
      publisher_id: values.publisher_id ?? null,
      role: values.role,
    };

    setSubmitting(true);
    try {
      await mutateAsync({
        resource: 'tenancy.organization_members',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
      onMembersChanged();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{ role: OrganizationRole.member }}
      >
        <Form.Item
          name="user_id"
          label={t('tenancy.organizations.fields.user_id')}
          rules={[{ required: true }]}
        >
          <Select
            {...userSelectProps}
            showSearch
            filterOption={false}
            placeholder={t('tenancy.organizations.fields.user_id_placeholder')}
          />
        </Form.Item>

        <Form.Item
          name="publisher_id"
          label={t('tenancy.organizations.fields.publisher')}
          extra={tk('publisher_hint')}
        >
          <Select
            allowClear
            loading={linksLoading || publishersQuery.isLoading}
            options={publisherOptions}
          />
        </Form.Item>

        <Form.Item
          name="role"
          label={t('tenancy.organizations.fields.role')}
          rules={[{ required: true }]}
        >
          <Radio.Group
            optionType="button"
            options={Object.values(OrganizationRole).map((value) => ({
              label: t(`tenancy.organizations.role.${value}`),
              value,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
